// Full-document render of the tempurpedic LegalPage: PageSkeleton (the React
// document shell) wrapping the legal-page layout (normal site chrome).
//
// Every top-level block is wrapped in `<div class="w-block-{type}
// block-{type}">` (load-bearing: _legal-page.scss keys on .block-image_link /
// .block-table), reproduced per top-level block via the per-block
// `StreamField tagName="div"` render.
import { Immutable } from "immer";
import React from "react";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";

import { templates } from "@reactivated";
import type {
    CharBlock as CharBlockValue,
    ImageChooserBlock as ImageChooserBlockValue,
    RawHTMLBlock as RawHTMLBlockValue,
    RichTextBlock as RichTextBlockValue,
    TableBlock as TableBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { PageSkeleton } from "../components/PageSkeleton";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
type IProps = templates.LegalPageTemplate;

type ILegalBlock = IProps["page"]["body"]["value"][number];
type IImageLinkValue = Extract<ILegalBlock, { type: "image_link" }>["value"];

// LegalPageBody block set: the four shared primitives plus `image_link` and
// `table`:
//   - `image_link` -> an <a> to the chosen page wrapping the image; eager
//     <img> from the serialized original-file URL per the shipped
//     eager-image convention;
//   - `table` (thelabui TableBlock, a ClientRenderingBarrier) -> the value is
//     the server-rendered wagtail table HTML string, raw-injected.
const bodyMap = {
    heading: (block: { value: Immutable<CharBlockValue> }) => (
        <>{block.value}</>
    ),
    paragraph: (block: { value: Immutable<RichTextBlockValue> }) => (
        <div dangerouslySetInnerHTML={{ __html: block.value }} />
    ),
    image: (block: { value: Immutable<ImageChooserBlockValue> }) => (
        // Max dimensions are expressed via inline style, not width/height
        // attrs: an explicit height attribute would pin the height when
        // max-width shrinks the width, distorting the aspect ratio on small
        // viewports. (image_link below does not set max dimensions, so it
        // carries no style.)
        <img
            alt={block.value.title}
            src={block.value.url}
            style={{
                maxWidth: `min(100%, ${block.value.width}px)`,
                maxHeight: `${block.value.height}px`,
            }}
            loading="lazy"
            decoding="async"
        />
    ),
    html: (block: { value: Immutable<RawHTMLBlockValue> }) => (
        <RawHTMLBlock value={block.value} />
    ),
    image_link: (block: { value: Immutable<IImageLinkValue> }) => {
        // alt + src only (see the `image` block above): .block-image_link's
        // max-width:100% would distort against an explicit height attribute,
        // so no max-dimensions style is set here either.
        const image = block.value.image ? (
            <img
                alt={block.value.image.title}
                src={block.value.image.url}
                loading="lazy"
                decoding="async"
            />
        ) : null;
        return block.value.page?.url ? (
            <a href={block.value.page.url}>{image}</a>
        ) : (
            image
        );
    },
    table: (block: { value: Immutable<TableBlockValue> }) =>
        block.value ? (
            <div dangerouslySetInnerHTML={{ __html: block.value }} />
        ) : null,
};

export const Template = (props: IProps): React.JSX.Element => {
    const { page } = props;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div id="legal-page" className="l-capped-width legal-page">
                <div className="content">
                    {page.show_title && <h1>{page.title}</h1>}
                    {page.body.value.map((block) => (
                        <StreamField
                            key={block.id}
                            tagName="div"
                            attrs={{
                                className: `w-block-${block.type} block-${block.type}`,
                            }}
                            value={[block]}
                            components={bodyMap}
                        />
                    ))}
                </div>
            </div>
        </PageSkeleton>
    );
};
